import { INDEXDB_COMMON_KEYS } from '@app/consts/common_consts';
import { ApiErrorResponse } from '@app/services/api';
import { Cache } from '../cache';
import { i18n, LanguageCode } from '../i18n-locales';
import { LANGUAGE_DEFAULT } from '../i18n-locales/i18n';

export enum TypeModal {
  /* SUCCESS */
  NORMAL = 'normal',
  WARNING = 'warning',
  ERROR = 'error',
  INFO = 'info',
  /* CONFIRM */
  INQUIRY = 'inquiry',
  NONE = '',
}

enum MessageType {
  WARNING = 'W',
  NORMAL = 'N',
  ERROR = 'E',
  INFO = 'I',
  INQUIRY = 'S',
  NONE = '',
}

export interface ErrorMessageProp {
  [key: string]: string;
}
export const getListMessageErrors = (): ErrorMessageProp => {
  const lang =
    i18n?.language && Object.values(LanguageCode).includes(i18n?.language as LanguageCode)
      ? i18n.language.toLowerCase()
      : LANGUAGE_DEFAULT;
  return (i18n.getResourceBundle(lang, 'messages') as ErrorMessageProp) || {};
};

export const MessageUtils = (() => {
  let messages: ErrorMessageProp = getListMessageErrors();

  // run at app level
  async function init(): Promise<void> {
    const { success, data: cacheValue } = await Cache.get<{
      [key: string]: string;
    }>(INDEXDB_COMMON_KEYS.ERROR_LIST, 'indexdb');
    // cache
    if (success && cacheValue) {
      messages = { ...messages, ...cacheValue.data };
    }
  }

  const get = (code: string, ...placeholders: (string | number)[]): string => {
    const bundle = { ...getListMessageErrors(), ...messages };
    let text = bundle[code] || `The message is not defined yet (${code})`;

    // 項目名:{0} 値:{1}）
    placeholders.forEach((p: string | number, index: number) => {
      text = text.replace(`{${index}}`, `${p}`);
    });
    return text;
  };

  const set = (list: ErrorMessageProp) => (messages = list);

  const getTypeFromCode = (code: string) => {
    // sample code: PC000233_E, PC000233_W, PC000233_I
    const prefixType = code?.split('_')[1][0];
    switch (prefixType) {
      case MessageType.INFO: // I
        return TypeModal.INFO;
      case MessageType.WARNING: // W
        return TypeModal.WARNING;
      case MessageType.ERROR: // E
        return TypeModal.ERROR;
      case MessageType.INQUIRY: // S
        return TypeModal.INQUIRY;
      case MessageType.NORMAL: // N
        return TypeModal.NORMAL;
      default:
        return TypeModal.NONE;
    }
  };

  /**
   * Selects the appropriate message based on current language preference
   * @param messageJp - Japanese message
   * @param messageEn - English message
   * @returns The message in the current language, with fallback to the other language
   */
  const selectMessageByLanguage = (messageJp?: string, messageEn?: string): string => {
    const isJapanese = i18n.language === LANGUAGE_DEFAULT;
    return isJapanese ? (messageJp ?? '') : (messageEn ?? '');
  };

  const formatApiMessage = (resp?: ApiErrorResponse | string): string => {
    // Suppress toast for handled redirects and cancelled requests
    if (resp instanceof Error && resp.name === 'HandledRedirectError') {
      const messageCode = (resp as any).messageCode as string;
      return messageCode ? get(messageCode) : '';
    }
    
    // Handle string code directly
    if (typeof resp === 'string') {
      return get(resp);
    }
    // If resp is undefined or null, return a generic error message
    if (!resp) {
      return i18n.t('E-COMMON-001', { ns: 'messages' });
    }
    // Priority 1: Use message from API response if available
    else if (resp && (resp.messageJp || resp.messageEn)) {
      return selectMessageByLanguage(resp.messageJp, resp.messageEn);
    }
    // Priority 2: Try to translate using error code
    else if (resp?.field) {
      return selectMessageByLanguage(resp.messageJp, resp.messageEn);
    }
    // Priority 3: Use error code to get message from i18n
    else {
      return i18n.t(resp.code, { ns: 'messages' });
    }
  };
  return {
    get,
    init,
    set,
    getTypeFromCode,
    formatApiMessage,
    selectMessageByLanguage,
  };
})();
